import { useMemo, useState } from 'react';

import {
  Button,
  SearchForm,
  TableContainer,
  TableHeader,
  Typography,
} from '@occc/ui-design-system';
import { ContainerColumnLeft } from '@occc-app/design-system';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { pluralize } from '@occc-app/lib/strings';

import { RetailerNetworkUserDetailsLocationsTable } from './retailer-network-user-details-locations-table.component';

import { useGetRetailerNetworkLocations } from '../../../../../data-access/queries';
import { useRetailerNetworkUserSelector } from '../../../../retailer-network-context/use-retailer-network-user.selector';

type RetailerNetworkUserDetailsLocationsTableContainerProps = {
  retailerNetworkUuid: string;
  selectLocationUuids: (locationUuids: string[]) => void;
  onClose: () => void;
};

export const RetailerNetworkUserDetailsLocationsTableContainer = ({
  retailerNetworkUuid,
  selectLocationUuids,
  onClose,
}: RetailerNetworkUserDetailsLocationsTableContainerProps) => {
  const [search, setSearch] = useState<string | undefined>(undefined);
  const { isRetailerNetworkSiteAdmin, retailerNetworkUserOpenCommerceUserUuid } =
    useRetailerNetworkUserSelector();
  const openCommerceUserUuidEquals = isRetailerNetworkSiteAdmin
    ? retailerNetworkUserOpenCommerceUserUuid
    : undefined;

  const { loading, error, data, paginationProps } = useGetRetailerNetworkLocations({
    filter: {
      search,
      retailerNetworkUuidEquals: retailerNetworkUuid,
      openCommerceUserUuidEquals,
    },
  });

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const onSubmit = () => {
    const newLocationUuids = Array.from(bulkSelectedItems);
    selectLocationUuids(newLocationUuids);

    onClose();
  };

  const headerActions: React.ReactNode = useMemo(() => {
    return (
      <SearchForm
        onSubmit={({ search }) => {
          setSearch(search);
        }}
      />
    );
  }, [setSearch]);

  const bulkSelectedItemsCount = bulkSelectedItems.size;

  return (
    <ContainerColumnLeft>
      <TableContainer>
        <TableHeader
          disableTitleTypography
          actions={headerActions}
          title={
            <Typography variant='h6' whiteSpace='nowrap'>
              Choose Locations to Add User to
            </Typography>
          }
          bulkSelected={bulkSelectedItemsCount}
          bulkActions={
            <Button color='primary' variant='contained' onClick={onSubmit}>
              {`Add selected ${pluralize(bulkSelectedItemsCount, 'Location')}`}
            </Button>
          }
        />
        <RetailerNetworkUserDetailsLocationsTable
          loading={loading}
          data={data}
          error={error}
          paginationProps={paginationProps}
          bulkSelectedItems={bulkSelectedItems}
          handleBulkSelectMultiple={handleBulkSelectMultiple}
          handleBulkDeselectAll={handleBulkDeselectAll}
          handleItemSelect={handleItemSelect}
        />
      </TableContainer>
    </ContainerColumnLeft>
  );
};
